import { teamInformationService } from '../services/teamInformationService';
import { teamMembersService } from '../services/teamMembersService';
import {
  TeamInformation,
  CreateTeamMemberData,
  PartnerContributorWithAccess,
} from '../types';
import { getVertivEmailVariants } from './emailNormalization';

/**
 * NEW DATABASE-FIRST TEAM MEMBER UTILITIES
 *
 * This completely eliminates the legacy format and works directly with the
 * team_information database structure. No more conversions = no more data loss.
 */

// ✅ PHASE 2 FIX: Added Default Sponsor roles to TeamRole type
export type TeamRole =
  | 'vertiv_sponsor'
  | 'vertiv_contributor'
  | 'partner_contributor'
  | 'default_sponsor_nam'
  | 'default_sponsor_emea'
  | 'default_sponsor_apac';

/**
 * Constant mapping of regions to their corresponding Default Sponsor roles
 * Used for optimized role lookups when useDefaultSponsors is enabled
 */
const REGION_TO_SPONSOR_ROLE_MAP: Record<string, TeamRole> = {
  nam: 'default_sponsor_nam',
  emea: 'default_sponsor_emea',
  apac: 'default_sponsor_apac',
} as const;

/**
 * Get all team members for a request, organized by role
 */
export const getTeamMembersByRequest = async (
  requestId: string,
): Promise<{
  vertivSponsors: TeamInformation[];
  vertivContributors: TeamInformation[];
  partnerContributors: TeamInformation[];
}> => {
  try {
    const teamMembers = await teamInformationService.getTeamMembers(requestId);
    return teamInformationService.organizeTeamMembersByRole(teamMembers);
  } catch (error) {
    console.error(
      `Failed to get team members for request ${requestId}:`,
      error,
    );
    return {
      vertivSponsors: [],
      vertivContributors: [],
      partnerContributors: [],
    };
  }
};

/**
 * Add team members to a specific role (preserves existing members in that role)
 */
export const addTeamMembersToRole = async (
  requestId: string,
  role: TeamRole,
  userIds: string[],
): Promise<TeamInformation[]> => {
  try {
    console.log(
      `Adding ${userIds.length} users to ${role} for request ${requestId}:`,
      userIds,
    );

    // Get all users to convert IDs to full user objects
    const allUsers = await teamMembersService.getAllTeamMembers();

    // Create new team member data
    const newMembers: CreateTeamMemberData[] = userIds
      .map(userId => {
        const user = allUsers.find(u => u.id === userId);
        if (!user) {
          console.warn(`User not found for ID: ${userId}`);
          return null;
        }
        return {
          userEmail: user.email,
          userFullName: user.name,
          userRole: role,
        };
      })
      .filter((member): member is CreateTeamMemberData => member !== null);

    if (newMembers.length === 0) {
      console.warn('No valid users found to add');
      return [];
    }

    // Add to database (this preserves existing members)
    const result = await teamInformationService.addTeamMembers(
      requestId,
      newMembers,
    );
    console.log(`Successfully added ${result.length} team members`);
    return result;
  } catch (error) {
    console.error(`Failed to add team members to ${role}:`, error);
    throw error;
  }
};

/**
 * Replace all team members for a specific role (removes existing, adds new)
 * LEGACY FUNCTION - USE replaceTeamMembersInRoleByEmail FOR NEW CODE
 */
export const replaceTeamMembersInRole = async (
  requestId: string,
  role: TeamRole,
  userIds: string[],
): Promise<TeamInformation[]> => {
  try {
    console.log(
      `Replacing ${role} members for request ${requestId} with:`,
      userIds,
    );

    // Get current team members
    const currentTeamMembers = await teamInformationService.getTeamMembers(
      requestId,
    );

    // Keep all members from other roles
    const otherRoleMembers = currentTeamMembers
      .filter(member => member.userRole !== role)
      .map(member => ({
        userEmail: member.userEmail,
        userFullName: member.userFullName,
        userRole: member.userRole,
      }));

    console.log(
      `Preserving ${otherRoleMembers.length} members from other roles`,
    );

    // Get all users for ID-to-user mapping
    const allUsers = await teamMembersService.getAllTeamMembers();

    // Create new members for the specified role
    const newRoleMembers: CreateTeamMemberData[] = userIds
      .map(userId => {
        const user = allUsers.find(u => u.id === userId);
        if (!user) {
          console.warn(`User not found for ID: ${userId}`);
          return null;
        }
        return {
          userEmail: user.email,
          userFullName: user.name,
          userRole: role,
        };
      })
      .filter((member): member is CreateTeamMemberData => member !== null);

    console.log(`Adding ${newRoleMembers.length} new ${role} members`);

    // Combine all members (other roles + new role members)
    const allMembers = [...otherRoleMembers, ...newRoleMembers];

    // Replace all team members with the combined list
    const result = await teamInformationService.replaceTeamMembers(
      requestId,
      allMembers,
    );

    console.log(`Successfully updated team members. Total: ${result.length}`);
    return result;
  } catch (error) {
    console.error(`Failed to replace ${role} members:`, error);
    throw error;
  }
};

/**
 * Replace all team members for a specific role using emails (EMAIL-BASED VERSION)
 */
export const replaceTeamMembersInRoleByEmail = async (
  requestId: string,
  role: TeamRole,
  userEmails: string[] | PartnerContributorWithAccess[],
): Promise<TeamInformation[]> => {
  try {
    console.log(
      `Replacing ${role} members for request ${requestId} with emails:`,
      userEmails,
    );

    // Get current team members
    const currentTeamMembers = await teamInformationService.getTeamMembers(
      requestId,
    );

    // Keep all members from other roles
    const otherRoleMembers = currentTeamMembers
      .filter(member => member.userRole !== role)
      .map(member => ({
        userEmail: member.userEmail,
        userFullName: member.userFullName,
        userRole: member.userRole,
        ...(member.accessLevel && { accessLevel: member.accessLevel }),
      }));

    console.log(
      `Preserving ${otherRoleMembers.length} members from other roles`,
    );

    // Get all users for email-to-user mapping
    const allUsers = await teamMembersService.getAllTeamMembers();

    // Create new members for the specified role using emails
    const newRoleMembers: CreateTeamMemberData[] = userEmails
      .map(emailOrObject => {
        // Handle both string[] and PartnerContributorWithAccess[]
        const userEmail =
          typeof emailOrObject === 'string'
            ? emailOrObject
            : emailOrObject.email;
        const accessLevel =
          typeof emailOrObject === 'object' && 'accessLevel' in emailOrObject
            ? emailOrObject.accessLevel
            : undefined;

        const user = allUsers.find(u => u.email === userEmail);
        if (!user) {
          console.warn(`User not found for email: ${userEmail}`);
          return null;
        }

        const memberData: CreateTeamMemberData = {
          userEmail: user.email,
          userFullName: user.name,
          userRole: role,
        };

        // Add access level for partner contributors
        if (accessLevel && role === 'partner_contributor') {
          memberData.accessLevel = accessLevel;
        }

        return memberData;
      })
      .filter((member): member is CreateTeamMemberData => member !== null);

    console.log(`Adding ${newRoleMembers.length} new ${role} members`);

    // Combine all members (other roles + new role members)
    const allMembers = [...otherRoleMembers, ...newRoleMembers];

    // Replace all team members with the combined list
    const result = await teamInformationService.replaceTeamMembers(
      requestId,
      allMembers,
    );

    console.log(`Successfully updated team members. Total: ${result.length}`);
    return result;
  } catch (error) {
    console.error(`Failed to replace ${role} members by email:`, error);
    throw error;
  }
};

/**
 * Remove specific team members by their team IDs
 */
export const removeTeamMembers = async (
  requestId: string,
  teamIds: number[],
): Promise<void> => {
  try {
    console.log(`Removing team members ${teamIds} from request ${requestId}`);

    for (const teamId of teamIds) {
      await teamInformationService.deleteTeamMember(requestId, teamId);
    }

    console.log(`Successfully removed ${teamIds.length} team members`);
  } catch (error) {
    console.error(`Failed to remove team members:`, error);
    throw error;
  }
};

/**
 * Clear all team members for a request
 */
export const clearAllTeamMembers = async (requestId: string): Promise<void> => {
  try {
    console.log(`Clearing all team members for request ${requestId}`);
    await teamInformationService.deleteAllTeamMembers(requestId);
    console.log('Successfully cleared all team members');
  } catch (error) {
    console.error('Failed to clear team members:', error);
    throw error;
  }
};

/**
 * Get user IDs for a specific role (for backward compatibility with UI components)
 * This is ONLY used to interface with existing UI components that expect user IDs
 */
export const getTeamMemberUserIds = async (
  requestId: string,
  role: TeamRole,
): Promise<string[]> => {
  try {
    const teamMembers = await teamInformationService.getTeamMembersByRole(
      requestId,
      role,
    );
    const allUsers = await teamMembersService.getAllTeamMembers();

    return teamMembers
      .map(member => {
        const user = allUsers.find(u => u.email === member.userEmail);
        return user?.id;
      })
      .filter((id): id is string => !!id);
  } catch (error) {
    console.error(`Failed to get user IDs for ${role}:`, error);
    return [];
  }
};

/**
 * Convert user IDs to team member data (for UI components that work with IDs)
 */
export const convertUserIdsToTeamMembers = async (
  userIds: string[],
): Promise<CreateTeamMemberData[]> => {
  try {
    const allUsers = await teamMembersService.getAllTeamMembers();

    return userIds
      .map(userId => {
        const user = allUsers.find(u => u.id === userId);
        if (!user) {
          console.warn(`User not found for ID: ${userId}`);
          return null;
        }
        return {
          userEmail: user.email,
          userFullName: user.name,
          userRole: 'vertiv_contributor' as TeamRole, // Default role, should be overridden
        };
      })
      .filter((member): member is CreateTeamMemberData => member !== null);
  } catch (error) {
    console.error('Failed to convert user IDs to team members:', error);
    return [];
  }
};

// =================================================================================
// EMAIL-BASED FORM PROCESSING FUNCTIONS
// These functions provide a simplified, email-first approach for form integration
// =================================================================================

/**
 * Load team members for form editing - returns email arrays for form compatibility
 * This is the email-based replacement for the legacy loadTeamMembersForRequest()
 */
export const loadTeamMembersForForm = async (
  requestId: string,
): Promise<{
  vertivSponsors: string[];
  vertivContributors: string[];
  partnerContributors: string[];
}> => {
  try {
    console.log(`Loading team members for form - request ${requestId}`);

    // Get team members directly from database
    const teamMembers = await teamInformationService.getTeamMembers(requestId);

    if (!teamMembers || teamMembers.length === 0) {
      console.log('No team members found, returning empty arrays');
      return {
        vertivSponsors: [],
        vertivContributors: [],
        partnerContributors: [],
      };
    }

    // Organize by role and extract emails
    const organized =
      teamInformationService.organizeTeamMembersByRole(teamMembers);

    const result = {
      vertivSponsors: organized.vertivSponsors.map(member => member.userEmail),
      vertivContributors: organized.vertivContributors.map(
        member => member.userEmail,
      ),
      partnerContributors: organized.partnerContributors.map(
        member => member.userEmail,
      ),
    };

    console.log(`Loaded team members for form:`, result);
    return result;
  } catch (error) {
    console.error(
      `Failed to load team members for form - request ${requestId}:`,
      error,
    );
    return {
      vertivSponsors: [],
      vertivContributors: [],
      partnerContributors: [],
    };
  }
};

/**
 * Parse a team-member field into a plain email array.
 *
 * Handles the three shapes the screens hold: a JSON-stringified array (editedData), an array
 * of emails, and an array of `{ email, accessLevel }` objects. Exported because the offline
 * queue has to store the same normalised shape the replay expects; the access level is NOT
 * preserved here, so partner contributors must go through `parsePartnerContributors`.
 */
export const parseTeamMemberEmails = (value: any): string[] => {
  if (Array.isArray(value)) {
    // Handle array of objects with email property (e.g., PartnerContributorWithAccess[])
    if (
      value.length > 0 &&
      typeof value[0] === 'object' &&
      value[0] !== null &&
      'email' in value[0]
    ) {
      return value
        .map(item => item.email)
        .filter(email => typeof email === 'string');
    }
    // Handle array of strings
    return value.filter(item => typeof item === 'string');
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        // Recursively handle the parsed array (could be objects or strings)
        return parseTeamMemberEmails(parsed);
      }
      return [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Parse a partner-contributor field WITHOUT collapsing it to emails.
 *
 * `parseTeamMemberEmails` throws the access level away, and a partner queued through it comes
 * back from the replay as read-only — a silent downgrade of a `read_write` partner. This keeps
 * whatever the caller held (emails or `{ email, accessLevel }` objects) so
 * `saveTeamMembersFromForm` can preserve the level.
 */
export const parsePartnerContributors = (
  value: any,
): Array<string | PartnerContributorWithAccess> => {
  if (Array.isArray(value)) {
    return value.filter(
      item =>
        typeof item === 'string' ||
        (typeof item === 'object' &&
          item !== null &&
          typeof item.email === 'string'),
    );
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsePartnerContributors(parsed) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const partnerEmailKey = (email: string): string => email.toLowerCase().trim();

const buildExistingPartnerLookup = (
  existingPartners: TeamInformation[],
): Map<string, TeamInformation> => {
  const lookup = new Map<string, TeamInformation>();
  for (const partner of existingPartners) {
    if (partner.userEmail) {
      lookup.set(partnerEmailKey(partner.userEmail), partner);
    }
  }
  return lookup;
};

type PartnerDirectoryUser = { email?: string; name?: string };

type ResolvedPartnerDirectoryUser = {
  email: string;
  name: string;
  /** Present only on the existing-row fallback. Directory matches leave this unset. */
  accessLevel?: 'read' | 'read_write';
};

/**
 * Resolve a partner email against the scoped directory, falling back to rows already
 * on the request. External requestors only see same-company partners in lookup, but
 * Vertiv-assigned out-of-domain partners must survive a partial deselect save (#1459).
 *
 * Fallback also carries the stored access level. A caller who cannot see the partner
 * in the directory has no basis to change it; stamping the form default `'read'`
 * 403s the whole team PUT (`partnerTeamWriteGuard`).
 */
const resolvePartnerDirectoryUser = (
  email: string,
  directoryUsers: PartnerDirectoryUser[],
  existingPartnerLookup: Map<string, TeamInformation>,
): ResolvedPartnerDirectoryUser | null => {
  const variants = getVertivEmailVariants(email);
  const directoryMatch = directoryUsers.find(user =>
    variants.includes(user.email?.toLowerCase() ?? ''),
  );
  if (directoryMatch?.email) {
    return {
      email: directoryMatch.email,
      name: directoryMatch.name ?? directoryMatch.email,
    };
  }

  const existing = existingPartnerLookup.get(partnerEmailKey(email));
  if (existing?.userEmail) {
    return {
      email: existing.userEmail,
      name: existing.userFullName ?? existing.userEmail,
      accessLevel: existing.accessLevel,
    };
  }

  return null;
};

/**
 * Save team members from form data - email-based approach
 * This is the email-based replacement for the legacy saveTeamMembers()
 */
export interface TeamFormData {
  vertivSponsors?: string[]; // email arrays from form
  vertivContributors?: string[];
  // Partner contributors may arrive as bare emails (legacy) or with their access level.
  // The offline queue stores the objects so the level survives the replay.
  partnerContributors?: Array<string | PartnerContributorWithAccess>;
  useDefaultSponsors?: boolean;
  region?: string;
}

/**
 * #1501: the row-building half of saveTeamMembersFromForm — returns the member
 * list ([] = clear all) or null when the directory lookups failed (parity with
 * the original's swallow-errors behavior). The aggregate create serializes
 * these rows into its payload; the legacy wrapper below keeps the PUT/DELETE.
 */
export const buildTeamMembersFromForm = async (
  requestId: string,
  formData: TeamFormData,
): Promise<CreateTeamMemberData[] | null> => {
  try {
    // Get all users from role-based endpoints to convert emails to full user objects
    const [
      sponsorUsers,
      contributorUsers,
      partnerUsers,
      defaultSponsorNAM,
      defaultSponsorEMEA,
      defaultSponsorAPAC,
      existingTeamByRole,
    ] = await Promise.all([
      teamMembersService.getUsersByRole('vertiv_sponsor'),
      teamMembersService.getUsersByRole('vertiv_contributor'),
      teamMembersService.getUsersByRole('partner_contributor'),
      teamMembersService.getUsersByRole('default_sponsor_nam'),
      teamMembersService.getUsersByRole('default_sponsor_emea'),
      teamMembersService.getUsersByRole('default_sponsor_apac'),
      getTeamMembersByRequest(requestId),
    ]);
    const existingPartnerLookup = buildExistingPartnerLookup(
      existingTeamByRole?.partnerContributors ?? [],
    );

    // Deduplicate users by email before combining
    const allUsersArray = [
      ...sponsorUsers,
      ...contributorUsers,
      ...partnerUsers,
      ...defaultSponsorNAM,
      ...defaultSponsorEMEA,
      ...defaultSponsorAPAC,
    ];
    const uniqueUsersMap = new Map();
    for (const user of allUsersArray) {
      if (!uniqueUsersMap.has(user.email)) {
        uniqueUsersMap.set(user.email, user);
      }
    }
    const allUsers = Array.from(uniqueUsersMap.values());

    // Helper function to convert email arrays to CreateTeamMemberData
    const convertEmailsToMembers = (
      emails: string[],
      role: TeamRole,
    ): CreateTeamMemberData[] => {
      // Deduplicate emails before processing
      const uniqueEmails = Array.from(new Set(emails));

      return uniqueEmails
        .map(email => {
          // Variant-aware email lookup (handles @vertiv.com vs @vertivco.com)
          const variants = getVertivEmailVariants(email);
          const user = allUsers.find(u =>
            variants.includes(u.email?.toLowerCase()),
          );
          if (!user) {
            console.warn(`User not found for email: ${email}`);
            return null;
          }
          return {
            userEmail: user.email,
            userFullName: user.name,
            userRole: role,
          };
        })
        .filter((member): member is CreateTeamMemberData => member !== null);
    };

    // Partner contributors carry an access level that convertEmailsToMembers cannot see,
    // because it only ever receives emails. Sending them through it defaults every partner
    // to 'read', which silently demotes a read_write partner on the offline replay.
    const convertPartnersToMembers = (
      partners: Array<string | PartnerContributorWithAccess>,
    ): CreateTeamMemberData[] => {
      // Deduplicate by email, keeping the last access level seen for that email
      const uniquePartners = new Map<string, 'read' | 'read_write'>();
      for (const partner of partners) {
        const email = typeof partner === 'string' ? partner : partner?.email;
        if (typeof email !== 'string') {
          continue;
        }
        const accessLevel =
          typeof partner === 'object' && partner?.accessLevel
            ? partner.accessLevel
            : 'read';
        uniquePartners.set(email, accessLevel);
      }

      return Array.from(uniquePartners.entries())
        .map(([email, accessLevel]): CreateTeamMemberData | null => {
          const user = resolvePartnerDirectoryUser(
            email,
            allUsers,
            existingPartnerLookup,
          );
          if (!user) {
            console.warn(`User not found for email: ${email}`);
            return null;
          }
          return {
            userEmail: user.email,
            userFullName: user.name,
            userRole: 'partner_contributor',
            accessLevel: user.accessLevel ?? accessLevel,
          };
        })
        .filter((member): member is CreateTeamMemberData => member !== null);
    };

    // Build complete team member list
    const allMembers: CreateTeamMemberData[] = [];

    // Determine the correct sponsor role based on useDefaultSponsors and region
    const getSponsorRole = (): TeamRole => {
      if (formData.useDefaultSponsors && formData.region) {
        const normalizedRegion = formData.region.toLowerCase().trim();
        const sponsorRole = REGION_TO_SPONSOR_ROLE_MAP[normalizedRegion];
        if (sponsorRole) {
          return sponsorRole;
        }
      }
      return 'vertiv_sponsor';
    };

    // Add sponsors
    if (formData.vertivSponsors) {
      const sponsorRole = getSponsorRole();
      const sponsors = convertEmailsToMembers(
        formData.vertivSponsors,
        sponsorRole,
      );
      allMembers.push(...sponsors);
    }

    // Add contributors
    if (formData.vertivContributors) {
      const contributors = convertEmailsToMembers(
        formData.vertivContributors,
        'vertiv_contributor',
      );
      allMembers.push(...contributors);
    }

    // Add partner contributors (access levels preserved)
    if (formData.partnerContributors) {
      const partners = convertPartnersToMembers(formData.partnerContributors);
      allMembers.push(...partners);
    }

    return allMembers;
  } catch (error) {
    console.error(
      `Failed to build team members from form for request ${requestId}:`,
      error,
    );
    // Null = skip the team save; callers keep the original swallow-errors behavior
    return null;
  }
};

export const saveTeamMembersFromForm = async (
  requestId: string,
  formData: TeamFormData,
): Promise<void> => {
  const allMembers = await buildTeamMembersFromForm(requestId, formData);
  if (allMembers === null) {
    return; // build failed — original behavior: never break the main operation
  }

  try {
    // If no team members, clear all
    if (allMembers.length === 0) {
      console.log(`Clearing all team members for request ${requestId}`);
      await teamInformationService.deleteAllTeamMembers(requestId);
      return;
    }

    // Replace all team members with new list
    console.log(
      `Saving ${allMembers.length} team members for request ${requestId}`,
    );
    await teamInformationService.replaceTeamMembers(requestId, allMembers);
    console.log(`Team members saved successfully for request ${requestId}`);
  } catch (error) {
    console.error(
      `Failed to save team members from form for request ${requestId}:`,
      error,
    );
    // Don't throw - we want the main request operation to succeed even if team members fail
  }
};

/**
 * Save team members from edited data (used in RequestDetail.tsx)
 * Similar to saveTeamMembersFromForm but handles JSON-stringified arrays from editedData
 */
export interface TeamEditedData {
  vertivSponsors?: string | string[];
  vertivContributors?: string | string[];
  partnerContributors?: string | string[];
  useDefaultSponsors?: boolean; // ✅ PHASE 3 FIX: Add useDefaultSponsors flag
  region?: string; // ✅ PHASE 3 FIX: Add region for Default Sponsor role detection
}

/**
 * #1501: the row-building half of saveAllTeamMembersFromEditedData — returns
 * the deduplicated member list (null = no team data provided, skip entirely).
 * The aggregate save serializes these rows into its payload; the legacy path
 * still PUTs them via the wrapper below. One builder, two transports.
 */
export const buildTeamMembersFromEditedData = async (
  requestId: string,
  editedData: TeamEditedData,
): Promise<CreateTeamMemberData[] | null> => {
  try {
    // Helper to parse email arrays (handles both string and array formats).
    // Hoisted to module scope as `parseTeamMemberEmails` so the offline queue can normalise
    // to the same shape; the alias keeps the rest of this function untouched.
    const parseEmailArray = parseTeamMemberEmails;

    // Get all users from role-based endpoints to convert emails to full user objects
    const [
      sponsorUsers,
      contributorUsers,
      partnerUsers,
      defaultSponsorNAM,
      defaultSponsorEMEA,
      defaultSponsorAPAC,
      existingTeamByRole,
    ] = await Promise.all([
      teamMembersService.getUsersByRole('vertiv_sponsor'),
      teamMembersService.getUsersByRole('vertiv_contributor'),
      teamMembersService.getUsersByRole('partner_contributor'),
      teamMembersService.getUsersByRole('default_sponsor_nam'),
      teamMembersService.getUsersByRole('default_sponsor_emea'),
      teamMembersService.getUsersByRole('default_sponsor_apac'),
      getTeamMembersByRequest(requestId),
    ]);
    const existingPartnerLookup = buildExistingPartnerLookup(
      existingTeamByRole?.partnerContributors ?? [],
    );

    // Deduplicate users by email before combining
    const allUsersArray = [
      ...sponsorUsers,
      ...contributorUsers,
      ...partnerUsers,
      ...defaultSponsorNAM,
      ...defaultSponsorEMEA,
      ...defaultSponsorAPAC,
    ];
    const uniqueUsersMap = new Map();
    for (const user of allUsersArray) {
      if (!uniqueUsersMap.has(user.email)) {
        uniqueUsersMap.set(user.email, user);
      }
    }
    const allUsers = Array.from(uniqueUsersMap.values());

    // Helper function to convert email arrays to CreateTeamMemberData
    const convertEmailsToMembers = (
      emails: string[],
      role: TeamRole,
    ): CreateTeamMemberData[] => {
      // Deduplicate emails before processing
      const uniqueEmails = Array.from(new Set(emails));

      return uniqueEmails
        .map(email => {
          // Variant-aware email lookup (handles @vertiv.com vs @vertivco.com)
          const variants = getVertivEmailVariants(email);
          const user = allUsers.find(u =>
            variants.includes(u.email?.toLowerCase()),
          );
          if (!user) {
            console.warn(`User not found for email: ${email}`);
            return null;
          }
          return {
            userEmail: user.email,
            userFullName: user.name,
            userRole: role,
          };
        })
        .filter((member): member is CreateTeamMemberData => member !== null);
    };

    // Helper function to convert partner contributors with access levels
    const convertPartnersToMembers = (
      partnersData: any,
    ): CreateTeamMemberData[] => {
      // Parse the partner contributors data (could be string or array)
      let partners: Array<{
        email: string;
        accessLevel?: 'read' | 'read_write';
      }> = [];

      if (typeof partnersData === 'string') {
        try {
          partners = JSON.parse(partnersData);
        } catch {
          console.warn('Failed to parse partner contributors data');
          return [];
        }
      } else if (Array.isArray(partnersData)) {
        partners = partnersData;
      }

      // Deduplicate partners by email, keeping the last access level for each unique email
      const uniquePartnersMap = new Map<
        string,
        { email: string; accessLevel: 'read' | 'read_write' }
      >();
      for (const partner of partners) {
        const email = typeof partner === 'string' ? partner : partner.email;
        if (typeof email !== 'string') {
          continue;
        }
        const accessLevel =
          typeof partner === 'object' && partner.accessLevel
            ? partner.accessLevel
            : 'read';
        uniquePartnersMap.set(email, { email, accessLevel });
      }
      const uniquePartners = Array.from(uniquePartnersMap.values());

      // Convert to CreateTeamMemberData with preserved access levels
      const members: CreateTeamMemberData[] = [];

      for (const partner of uniquePartners) {
        const user = resolvePartnerDirectoryUser(
          partner.email,
          allUsers,
          existingPartnerLookup,
        );
        if (!user) {
          console.warn(`User not found for email: ${partner.email}`);
          continue;
        }

        members.push({
          userEmail: user.email,
          userFullName: user.name,
          userRole: 'partner_contributor' as TeamRole,
          accessLevel: user.accessLevel ?? partner.accessLevel,
        });
      }

      return members;
    };

    // Build complete team member list from all roles with IMMEDIATE deduplication
    // Use a Map to ensure only one entry per email+role combination
    const membersByEmailAndRole = new Map<string, CreateTeamMemberData>();

    // Determine the correct sponsor role based on useDefaultSponsors and region
    const getSponsorRole = (): TeamRole => {
      if (editedData.useDefaultSponsors && editedData.region) {
        const normalizedRegion = editedData.region.toLowerCase().trim();
        const sponsorRole = REGION_TO_SPONSOR_ROLE_MAP[normalizedRegion];
        if (sponsorRole) {
          return sponsorRole;
        }
      }
      return 'vertiv_sponsor';
    };

    // Add sponsors - deduplicate by email within role
    if (editedData.vertivSponsors !== undefined) {
      const sponsorEmails = parseEmailArray(editedData.vertivSponsors);
      const uniqueSponsorEmails = Array.from(new Set(sponsorEmails));
      const sponsorRole = getSponsorRole();
      const sponsors = convertEmailsToMembers(uniqueSponsorEmails, sponsorRole);

      sponsors.forEach(sponsor => {
        const key = `${sponsor.userEmail}:${sponsorRole}`;
        membersByEmailAndRole.set(key, sponsor);
      });
    }

    // Add contributors - deduplicate by email within role
    if (editedData.vertivContributors !== undefined) {
      const contributorEmails = parseEmailArray(editedData.vertivContributors);
      const uniqueContributorEmails = Array.from(new Set(contributorEmails));
      const contributors = convertEmailsToMembers(
        uniqueContributorEmails,
        'vertiv_contributor',
      );

      contributors.forEach(contributor => {
        const key = `${contributor.userEmail}:vertiv_contributor`;
        membersByEmailAndRole.set(key, contributor);
      });
      console.log(
        `Processing ${uniqueContributorEmails.length} unique contributor emails`,
      );
    }

    // Add partner contributors (with access levels preserved) - deduplicate by email within role
    if (editedData.partnerContributors !== undefined) {
      const partners = convertPartnersToMembers(editedData.partnerContributors);

      partners.forEach(partner => {
        const key = `${partner.userEmail}:partner_contributor`;
        const existing = membersByEmailAndRole.get(key);

        // If partner already exists for this email, keep the one with higher access level
        if (
          !existing ||
          (partner.accessLevel === 'read_write' &&
            existing.accessLevel === 'read')
        ) {
          membersByEmailAndRole.set(key, partner);
        }
      });
      console.log(`Processing ${partners.length} partner contributors`);
    }

    // If no team members provided, don't clear (only update if data was provided)
    if (
      membersByEmailAndRole.size === 0 &&
      editedData.vertivSponsors === undefined &&
      editedData.vertivContributors === undefined &&
      editedData.partnerContributors === undefined
    ) {
      console.log(
        'No team member data provided in editedData, skipping update',
      );
      return null;
    }

    // Convert Map to array - already deduplicated
    return Array.from(membersByEmailAndRole.values());
  } catch (error) {
    console.error(
      `Failed to save team members from edited data for request ${requestId}:`,
      error,
    );
    throw error; // Re-throw so caller can handle
  }
};

export const saveAllTeamMembersFromEditedData = async (
  requestId: string,
  editedData: TeamEditedData,
): Promise<void> => {
  const uniqueMembers = await buildTeamMembersFromEditedData(
    requestId,
    editedData,
  );
  if (uniqueMembers === null) {
    return;
  }

  // Replace all team members with new list (ONE PUT request)
  console.log(
    `Saving ${uniqueMembers.length} team members for request ${requestId} in ONE request`,
  );
  await teamInformationService.replaceTeamMembers(requestId, uniqueMembers);
  console.log(`Team members saved successfully for request ${requestId}`);
};

/**
 * Check if form data contains team member changes (direct port from legacy)
 */
export const hasTeamMemberChanges = (formData: any): boolean => {
  return (
    formData.vertivSponsors !== undefined ||
    formData.vertivContributors !== undefined ||
    formData.partnerContributors !== undefined
  );
};

/**
 * Extract team member data from form data (direct port from legacy)
 */
export const extractTeamMemberData = (
  formData: any,
): {
  vertivSponsors?: string[];
  vertivContributors?: string[];
  partnerContributors?: string[];
} => {
  const teamData: any = {};

  if (formData.vertivSponsors !== undefined) {
    teamData.vertivSponsors = formData.vertivSponsors;
  }
  if (formData.vertivContributors !== undefined) {
    teamData.vertivContributors = formData.vertivContributors;
  }
  if (formData.partnerContributors !== undefined) {
    teamData.partnerContributors = formData.partnerContributors;
  }

  return teamData;
};
